import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentUploadZone = ({ onUpload }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [processingStatus, setProcessingStatus] = useState({});

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleChange = (e) => {
    e?.preventDefault();
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFiles(e?.target?.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const newFiles = fileArray?.map(file => ({
      id: Date.now() + Math.random(),
      name: file?.name,
      size: file?.size,
      type: file?.type,
      uploadedAt: new Date()?.toISOString()
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    newFiles?.forEach(file => {
      setProcessingStatus(prev => ({ ...prev, [file?.id]: 'processing' }));
      
      setTimeout(() => {
        setProcessingStatus(prev => ({ ...prev, [file?.id]: 'completed' }));
      }, 2000);
    });

    if (onUpload) {
      onUpload(newFiles);
    }
  };

  const handleRemove = (fileId) => {
    setUploadedFiles(prev => prev?.filter(f => f?.id !== fileId));
    setProcessingStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus?.[fileId];
      return newStatus;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const getFileIcon = (type) => {
    if (type?.includes('pdf')) return 'FileText';
    if (type?.includes('image')) return 'Image';
    if (type?.includes('word') || type?.includes('document')) return 'FileText';
    if (type?.includes('excel') || type?.includes('spreadsheet')) return 'Table';
    return 'File';
  };

  return (
    <div className="space-y-16">
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-24 md:p-32
          transition-smooth text-center
          ${dragActive 
            ? 'border-primary bg-primary/5' :'border-border bg-muted/30 hover:border-primary/50'
          }
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          multiple
          onChange={handleChange}
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
        />
        
        <div className="flex flex-col items-center gap-12">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="Upload" size={32} className="text-primary" />
          </div>
          
          <div>
            <h3 className="text-base font-semibold text-foreground mb-4">
              Upload Datasheets & Documents
            </h3>
            <p className="text-sm text-muted-foreground mb-12">
              Drag and drop files here, or click to browse
            </p>
            <Button
              variant="outline"
              iconName="FolderOpen"
              iconPosition="left"
              iconSize={18}
              onClick={() => document.getElementById('file-upload')?.click()}
            >
              Browse Files
            </Button>
          </div>

          <div className="text-xs text-muted-foreground caption">
            Supported formats: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG (Max 10MB)
          </div>
        </div>
      </div>
      {uploadedFiles?.length > 0 && (
        <div className="space-y-8">
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-8">
            <Icon name="Files" size={16} />
            Uploaded Documents ({uploadedFiles?.length})
          </h4>
          
          <div className="space-y-8">
            {uploadedFiles?.map((file) => (
              <div
                key={file?.id}
                className="bg-card border border-border rounded-lg p-12 md:p-16 flex items-center gap-12"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name={getFileIcon(file?.type)} size={20} className="text-primary" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-8 mb-4">
                    <h5 className="text-sm font-medium text-foreground truncate">{file?.name}</h5>
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName="X"
                      iconSize={16}
                      onClick={() => handleRemove(file?.id)}
                    />
                  </div>
                  
                  <div className="flex items-center gap-12 text-xs text-muted-foreground caption">
                    <span>{formatFileSize(file?.size)}</span>
                    <span>•</span>
                    <span>{new Date(file.uploadedAt)?.toLocaleString()}</span>
                  </div>

                  {processingStatus?.[file?.id] && (
                    <div className="mt-8">
                      {processingStatus?.[file?.id] === 'processing' ? (
                        <div className="flex items-center gap-8">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary animate-pulse" style={{ width: '60%' }} />
                          </div>
                          <span className="text-xs text-primary caption flex items-center gap-4">
                            <Icon name="Sparkles" size={12} />
                            AI Processing...
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-8 text-xs text-success">
                          <Icon name="CheckCircle2" size={14} />
                          <span>Processing Complete</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUploadZone;